import { useState  } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import PaymentWindow from "./paymentWindow";

export const useFormSubmission = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  
  const handleFormSubmission = async (formData, config) => {
    const { name, apiEndpoint, formType, successRoute, email } = config;
    setFormConfig(config);

    const userConfirmed = await Swal.fire({
      title: "Are you sure?",
      text: "Please review your data before proceeding.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Cancel"
    });

    if (!userConfirmed.isConfirmed) return;

    setIsSubmitting(true);

    try {
      // Submit main form data
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();

  // If response is not OK (400/500 errors)
  if (!response.ok) {
    // Handle validation errors (400 Bad Request)
    if (result.errors) {
      const errorMessages = Object.values(result.errors)
        .map(message => `• ${message}`)
        .join('<br>');
  
      await Swal.fire({
        title: "Form Errors",
        html: `Please correct these issues:<br><br>${errorMessages}`,
        icon: "error",
        confirmButtonText: "OK"
      });
      return;
    }
  
    // If no 'errors' field, show raw backend message or status text
    const backendMessage = result.message || response.statusText || "Something went wrong. Please try again.";
    
    await Swal.fire({
      title: "Submission Failed",
      text: backendMessage,
      icon: "error",
      confirmButtonText: "OK"
    });
  
    return;
  }
  


      const uniqueFormId = result.resultId;
      const amount = result.amount;

      // Generate payment QR
      const paymentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paymentService/generate-upi-qr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, formType, uniqueFormId, amount })
      });

      const paymentData = await paymentResponse.json();

      setPaymentData({
        isOpen: true,
        qrData: paymentData.qr,
        amount,
        originalAmount: amount,
        upiLink: paymentData.upiLink,
        name,
        formType,
        uniqueFormId
      });

    } catch (error) {
        console.error("Submission error:", error);
        Swal.fire({
            title: 'Error',
            text: error.message,
            icon: 'error'
        });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentComplete = async (transactionId, couponCode = null) => {
    try {
      setIsSubmitting(true);

      // Prepare payment confirmation data
    const paymentConfirmation = {
      name: paymentData.name,
      email: formConfig.email,
      formType: paymentData.formType,
      uniqueFormId: paymentData.uniqueFormId,
      amount: paymentData.amount,
      transactionId
    };
       // Include coupon code if one was applied
    if (couponCode) {
      paymentConfirmation.couponCode = couponCode;
    }
  
      // Send confirmation with transaction ID
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paymentService/confirm-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentConfirmation)
      });

      if (!response.ok) throw new Error("Payment confirmation failed");

      setPaymentData(null);

      if (response.isFree) {
        Swal.fire("Success", "Free payment confirmed successfully!", "success");
      } else {
        Swal.fire("Success", "Data Submitted Successfully", "success");
      }
      localStorage.clear();

      setTimeout(() => {
        navigate(`${formConfig?.successRoute}/${paymentData.uniqueFormId}` || "/");
      }, 100);


    } catch (error) {
      Swal.fire("Error", error.message || "Payment confirmation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCouponApply = async (couponCode) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paymentService/apply_coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          couponCode,
          name: paymentData.name,
          formType: paymentData.formType,
          uniqueFormId: paymentData.uniqueFormId,
          originalAmount: paymentData.originalAmount 
        })
      });
  
      const data = await response.json();
  
      if (data.success) {
        setPaymentData(prev => ({
          ...prev,
          amount: data.discountedAmount,
          qrData: data.newQrCode,
          upiLink: data.upiLink
          }));
          

          
        if (data.isFree) {
          await handlePaymentComplete(`FREE-${Date.now()}`, couponCode);
          return true;
        }


        return true;
      }
  
      return false;
    } catch (error) {
      return false;
    }
  };

  
  const handlePaymentCancel = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to close the payment gateway?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, close it",
      cancelButtonText: "No, stay"
    });
  
    if (result.isConfirmed) {
      setPaymentData(null);
      Swal.fire("Closed", "Payment window was closed", "info");
    }
  };

  const PaymentWindowComponent = paymentData?.isOpen ? (
    <PaymentWindow
      isOpen={paymentData.isOpen}
      qrData={paymentData.qrData}
      amount={paymentData.amount}
      upiLink={paymentData.upiLink}
      onConfirm={handlePaymentComplete}
      onClose={handlePaymentCancel}
      onCouponApply={handleCouponApply}
      isSubmitting={isSubmitting}
    />
  ) : null;

  return { 
    handleFormSubmission, 
    isSubmitting, 
    PaymentWindowComponent 
  };
};